/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import errorFallbackStyles from '@app/components/ErrorFallback.module.css';
import {copy as copyText} from '@app/actions/TextCopyActionCreators';
import {FluxerIcon} from '@app/components/icons/FluxerIcon';
import {NativeTitlebar} from '@app/components/layout/NativeTitlebar';
import {Button} from '@app/components/uikit/button/Button';
import {useNativePlatform} from '@app/hooks/useNativePlatform';
import AppStorage from '@app/lib/AppStorage';
import {Logger} from '@app/lib/Logger';
import {ensureLatestAssets} from '@app/lib/Versioning';
import GatewayConnectionStore from '@app/stores/gateway/GatewayConnectionStore';
import LayerManager from '@app/stores/LayerManager';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import {i18n} from '@lingui/core';
import {Trans} from '@lingui/react/macro';
import type React from 'react';
import {useCallback, useEffect, useState} from 'react';

interface ErrorFallbackProps {
	error?: Error;
	reset?: () => void;
}

const logger = new Logger('ErrorFallback');
const PRESERVED_RESET_STORAGE_KEYS = ['DraftStore'] as const;

function getStackTraceText(error?: Error): string | null {
	if (!error) {
		return null;
	}

	const stack = error.stack?.trim();
	if (stack) {
		return stack;
	}

	const name = error.name?.trim() || 'Error';
	const message = error.message?.trim() || 'Unknown error';
	return `${name}: ${message}`;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({error}) => {
	const {platform, isNative, isMacOS} = useNativePlatform();
	const [updateAvailable, setUpdateAvailable] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isCopyingStackTrace, setIsCopyingStackTrace] = useState(false);
	const [checkingForUpdates, setCheckingForUpdates] = useState(true);
	const stackTraceText = getStackTraceText(error);

	useEffect(() => {
		try {
			GatewayConnectionStore.logout();
			LayerManager.closeAll();
			MediaEngineStore.cleanup();
		} catch (error) {
			logger.error('Failed to clean up runtime state on crash screen', error);
		}
	}, []);

	useEffect(() => {
		let isMounted = true;

		const run = async () => {
			try {
				const {updateFound} = await ensureLatestAssets({force: true});
				if (isMounted) {
					setUpdateAvailable(updateFound);
				}
			} catch (error) {
				logger.error('Failed to check for updates:', error);
			} finally {
				if (isMounted) {
					setCheckingForUpdates(false);
				}
			}
		};

		void run();

		return () => {
			isMounted = false;
		};
	}, []);

	const handleUpdate = useCallback(async () => {
		setIsUpdating(true);
		try {
			const {updateFound} = await ensureLatestAssets({force: true});
			if (!updateFound) {
				setIsUpdating(false);
				window.location.reload();
			}
		} catch (error) {
			logger.error('Failed to apply update:', error);
			setIsUpdating(false);
		}
	}, []);

	const handleCopyStackTrace = useCallback(async () => {
		if (!stackTraceText) {
			return;
		}

		setIsCopyingStackTrace(true);
		try {
			await copyText(i18n, stackTraceText);
		} finally {
			setIsCopyingStackTrace(false);
		}
	}, [stackTraceText]);

	return (
		<div className={errorFallbackStyles.errorFallbackContainer}>
			{isNative && !isMacOS && <NativeTitlebar platform={platform} />}
			<FluxerIcon className={errorFallbackStyles.errorFallbackIcon} />
			<div className={errorFallbackStyles.errorFallbackContent}>
				<h1 className={errorFallbackStyles.errorFallbackTitle}>
					<Trans>Whoa, this is heavy.</Trans>
				</h1>
				<p className={errorFallbackStyles.errorFallbackDescription}>
					{checkingForUpdates ? (
						<Trans>The app has crashed. Checking for updates that might fix this issue...</Trans>
					) : updateAvailable ? (
						<Trans>Something went wrong and the app crashed. An update is available that may fix this issue.</Trans>
					) : (
						<Trans>Something went wrong and the app crashed. Try reloading or resetting the app.</Trans>
					)}
				</p>
			</div>
			<div className={errorFallbackStyles.errorFallbackActions}>
				<Button
					onClick={updateAvailable ? handleUpdate : () => location.reload()}
					disabled={checkingForUpdates}
					submitting={isUpdating}
				>
					{checkingForUpdates || updateAvailable ? <Trans>Update app</Trans> : <Trans>Reload app</Trans>}
				</Button>
				{stackTraceText && (
					<Button onClick={handleCopyStackTrace} submitting={isCopyingStackTrace} variant="secondary">
						<Trans>Copy stack trace</Trans>
					</Button>
				)}
				<Button
					onClick={() => {
						AppStorage.clearExcept(PRESERVED_RESET_STORAGE_KEYS);
						location.reload();
					}}
					variant="danger-primary"
					disabled={checkingForUpdates}
				>
					<Trans>Reset app data</Trans>
				</Button>
			</div>
		</div>
	);
};
